import { PalmettoToastContainer, PalmettoToaster } from '@palmetto/dsm-components';
import { Themes } from '@palmetto/dsm-tokens';
import { ResponsiveProvider } from '@palmetto/palmetto-components';
import { PrismicProvider } from '@prismicio/react';
import { Outlet } from '@tanstack/react-router';
import classNames from 'classnames';
import { client } from '../../../api/prismic/prismic';
import useAmplitudeEngagement from '../../../hooks/_app/useAmplitudeEngagement';
import useDatadog from '../../../hooks/_app/useDatadog';
import useInAppReview from '../../../hooks/_app/useInAppReview';
import useAppListener from '../../../hooks/_app/useNativeAppListener';
import usePlaidSessionRecovery from '../../../hooks/_app/usePlaidSessionRecovery';
import usePlaidUrlInterceptor from '../../../hooks/_app/usePlaidUrlInterceptor';
import { usePushNotifications } from '../../../hooks/_app/usePushNotifications';
import useThemedFavicon from '../../../hooks/_app/useThemedFavicon';
import { useArticleModal } from '../../../hooks/useArticleModal';
import { useIntercomArticleDeepLink } from '../../../hooks/useIntercomArticleDeepLink';
import { KeyboardProvider } from '../../../hooks/useKeyboardState';
import CustomerTermsModal from '../../_modals/CustomerTermsModal/CustomerTermsModal';
import { Tracking } from '@helios/frontend-core';
import { LiveUpdatePrompt } from '../LiveUpdatePrompt/LiveUpdatePrompt';
import { ModalProvider } from '../../Modal/Modal';
import { ModalV3Provider, ModalV3Renderer } from '../../ModalV3/ModalV3Provider';
import { MainNavLayout } from '../../Navigation/MainNavLayout';
import { AchievementTakeoverListener } from '../AchievementTakeoverListener/AchievementTakeoverListener';
import { CurrentPageProvider } from '../CurrentPageProvider/CurrentPageProvider';
import { PageErrorProvider } from '../PageErrorProvider/PageErrorProvider';
import { TakeoverProvider } from '../TakeoverProvider/TakeoverProvider';

import { useWebhookForceReload } from '../../../hooks/_app/useWebviewForceReload';
import styles from './App.module.scss';

export const Root: React.FC = () => {
  useThemedFavicon();
  useInAppReview(true);
  useDatadog();
  usePushNotifications();
  useAppListener();
  usePlaidUrlInterceptor();
  usePlaidSessionRecovery();
  useAmplitudeEngagement();
  useArticleModal();
  useIntercomArticleDeepLink();
  useWebhookForceReload();

  return (
    <CurrentPageProvider>
      <PageErrorProvider>
        <ResponsiveProvider>
          <KeyboardProvider>
            <ModalProvider>
              <PrismicProvider client={client}>
                <Tracking>
                  <ModalV3Provider>
                    <TakeoverProvider>
                      <AchievementTakeoverListener />
                      <div
                        id="app-container"
                        data-theme={Themes.core}
                        className={classNames(styles.pageContainer, 'body-400 overflow-hidden')}
                      >
                        <MainNavLayout>
                          <Outlet />
                        </MainNavLayout>
                        <CustomerTermsModal />
                        <LiveUpdatePrompt />
                        <PalmettoToastContainer toaster={PalmettoToaster} />
                      </div>
                      <ModalV3Renderer />
                    </TakeoverProvider>
                  </ModalV3Provider>
                </Tracking>
              </PrismicProvider>
            </ModalProvider>
          </KeyboardProvider>
        </ResponsiveProvider>
      </PageErrorProvider>
    </CurrentPageProvider>
  );
};
